import { Quill } from "react-quill-new";
import { EmbedBlot } from "parchment";
import Delta from "quill-delta";

const SOFTBREAK_REGISTERED_KEY = "__quill_softbreak_registered__";

/** Format / blot name used in Delta & formats whitelist. */
export const SOFTBREAK_BLOT = "softbreak";

/**
 * Soft line break inside a block (`<br class="ql-softbreak">`).
 * Distinct from Quill's zero-length empty-block Break (`<p><br></p>`).
 */
class SoftBreakBlot extends EmbedBlot {
  static blotName = SOFTBREAK_BLOT;
  static tagName = "BR";
  static className = "ql-softbreak";

  static value() {
    return true;
  }

  length() {
    return 1;
  }
}

export const ensureSoftBreakRegistered = () => {
  const globalRef = globalThis as typeof globalThis & Record<string, unknown>;
  if (globalRef[SOFTBREAK_REGISTERED_KEY]) return;

  (Quill as unknown as { register: (blot: unknown, overwrite?: boolean) => void }).register(
    SoftBreakBlot,
    true,
  );
  globalRef[SOFTBREAK_REGISTERED_KEY] = true;
};

type SoftBreakRange = { index: number; length: number };

/**
 * Shift+Enter: insert soft `<br>` instead of a new paragraph.
 * At end of a block the browser ignores a trailing BR, so a second one is inserted
 * so the caret stays visible on the new visual line.
 */
export function softBreakKeyboardHandler(
  this: { quill: InstanceType<typeof Quill> },
  range: SoftBreakRange,
) {
  const quill = this.quill;
  if (!quill || range == null) return;

  if (range.length > 0) {
    quill.deleteText(range.index, range.length, "user");
  }

  const [currentLeaf] = quill.getLeaf(range.index);
  const [nextLeaf] = quill.getLeaf(range.index + 1);

  quill.insertEmbed(range.index, SOFTBREAK_BLOT, true, "user");

  if (nextLeaf == null || (currentLeaf != null && currentLeaf.parent !== nextLeaf.parent)) {
    quill.insertEmbed(range.index, SOFTBREAK_BLOT, true, "user");
  }

  quill.setSelection(range.index + 1, 0, "silent");
}

/** Clipboard: mid-block `<br>` → softbreak; lone `<br>` in a block stays Quill empty-line. */
export const softBreakClipboardMatcher = (
  node: Node,
  delta: InstanceType<typeof Delta>,
): InstanceType<typeof Delta> => {
  const parent = node.parentNode;
  if (parent && parent.childNodes.length === 1) {
    return delta;
  }
  return new Delta().insert({ [SOFTBREAK_BLOT]: true });
};

export const softBreakKeyboardBinding = {
  key: "Enter" as const,
  shiftKey: true,
  handler: softBreakKeyboardHandler,
};
